// #region Imports
import { traverserStopCellTint, traverserStopRowBorderPulse } from "_shared/_views/highlights/traverser-stop-highlight";
import { traverserStopClassName } from "_shared/_views/highlights/traverser-stop-scope";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import SearchIcon from "@mui/icons-material/Search";
import SnippetFolderIcon from "@mui/icons-material/SnippetFolder";
import UndoIcon from "@mui/icons-material/Undo";
import { green } from "@mui/material/colors";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { alpha, styled, useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { skipToken } from "@reduxjs/toolkit/query";
import { feeScheduleCategoryTypeLabel } from "_shared/fee-schedules/categories/types/fee-schedule-category-type";
import FeeScheduleCategoryTypeText from "_shared/fee-schedules/categories/types/fee-schedule-category-type-text";
import FeeScheduleCategoryDescription from "_shared/fee-schedules/categories/descriptions/fee-schedule-category-description";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import FeeScheduleTeamRoster from "_shared/fee-schedules/categories/fee-schedule-team-roster";
import FeeScheduleDepartedTeamMember, {
  departedRateCellStyles,
  departureOfRate,
  indexDeparturesByUserId,
  type FeeScheduleTeamMemberDeparture,
} from "_shared/fee-schedules/categories/_views/fee-schedule-departed-rate";
import FeeScheduleIssueFlag, {
  feeScheduleOffTeamFlagText,
  type FeeScheduleIssueFlagSeverity,
} from "_shared/fee-schedules/categories/_views/fee-schedule-issue-flag";
import { ProposalIssueLevel, ProposalIssuePart, type ProposalIssue } from "work/proposals/issues/proposal-issues";
import { feeScheduleIssueCellKey } from "work/proposals/issues/fee-schedule-issue-cell";
import Fee from "_shared/fee-schedules/categories/fees/fee";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import Guid from "_shared/guids/guid";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import {
  createMRTColumnHelper,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_PaginationState as PaginationState,
  type MRT_Row,
  type MRT_SortingState as SortingState,
  MRT_TableInstance,
  MRT_TableOptions,
  type MRT_Updater,
} from "material-react-table";
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { FeeScheduleCategoryMaskedCurrency } from "work/masked-fields";
import ProposalFieldHistoryButton from "work/proposals/negotiation/proposal-field-history-button";
import ProposalFieldTimelineDropdown, {
  type ProposalFieldTimelineRevertEntry,
} from "work/proposals/negotiation/proposal-field-timeline-dropdown";
import ProposalDeleteActionButton from "work/proposals/_views/_shared/proposal-delete-action-button";
import {
  proposalCollectionItemRevertAspect,
  proposalCollectionItemRevertValue,
} from "work/proposals/negotiation/proposal-collection-item-revert";
import {
  proposalFeeScheduleAspectOptions,
  proposalFeeScheduleRestrictedTeamMemberText,
  proposalFeeScheduleTeamMemberFallbackText,
  proposalFeeScheduleUnassignedText,
} from "work/proposals/negotiation/proposal-fee-schedule-aspects";
import {
  REMOVED_ROW_REDLINE_ATTRIBUTE,
  RemovedRowRedlineTone,
  removedRowRedlineColour,
  removedRowRedlineStyles,
  removedRowRedlineTone,
} from "work/proposals/redlining/field-redlines/_views/removed-row-redline";
import { ownStrikeAttribute } from "work/proposals/redlining/__diff/own-strike";
import { useRemovedProposalFeeScheduleCategories } from "work/proposals/negotiation/proposal-fee-schedule-removed-categories";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import FeeRedlineInput from "work/proposals/redlining/fee-schedule-redline/fee-redline/_views/fee-redline-input";
import { FeeScheduleCategoryRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-category-redline";
import { feeScheduleTeamMemberRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-team-member-redline";
import { FeeScheduleRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-redline";
import {
  RedlineCell,
  RedlineCellValue,
  redlineCellClasses,
} from "work/proposals/redlining/field-redlines/_views/redline-cell";
import TextRedlineInput from "work/proposals/redlining/field-redlines/_views/text-redline-input";
import SearchMatch from "_shared/_views/tables/search-match";
import { MRTNoMemo } from "_shared/_views/tables/table";
import TableAddActions, { type TableAddAction } from "_shared/_views/tables/table-add-actions";
import RowActions, { type RowAction } from "_shared/_views/tables/row-actions";
import { paneStretchTable } from "_shared/_views/tables/table-pane-stretch";
import {
  CollectionToolbar,
  CollectionToolbarSearch,
  CollectionToolbarViewButtons,
} from "_shared/_views/tables/table-toolbar";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";
import Text from "_shared/text/text";
import {
  FeeScheduleTableCellText,
  feeScheduleTableCellStyles,
} from "_shared/fee-schedules/categories/_views/fee-schedule-table-cell";
import { feeScheduleColumnWidth } from "work/fee-schedule-templates/_views/tables/fee-schedule-table-columns";
import { ReviewerEditLockIcon } from "work/proposals/reviewers/_views/reviewer-edit-lock-feedback";
import {
  paneIsCompact,
} from "work/_views/work-pane-layout";
import {
  useWorkPaneIsCompact,
} from "work/_views/use-work-pane-size";
// #endregion

// #region Styles
const ROW_ACTIONS_COLUMN_ID = "mrt-row-actions";
/** The columns a fee schedule redline is drawn in, and so the ones a highlighted row's box encloses. */
const REDLINEABLE_COLUMN_IDS: ReadonlySet<string> = new Set(["name", "description", "type", "fee", "teamMember"]);
const RowActionsContainer = styled("div")(() => ({
  whiteSpace: "nowrap",
}));
const LockedCellContent = styled("span")({
  alignItems: "center",
  display: "inline-flex",
  justifyContent: "center",
  lineHeight: 0,
  verticalAlign: "middle",
  width: "100%",
});
const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
}));
/**
 * The member a removed category was assigned to, in the disabled tone. This cell had gone blank
 * instead, which read as "never assigned" rather than "removed along with the rest of the row". The
 * row strikes it with its other cells (`removed-row-redline.ts`).
 *
 * A member who has left the team is the exception: they were struck when they left, a settled change,
 * so their line stays in the text tone whatever colour the category's own removal is (`own-strike.ts`).
 */
const RemovedTeamMember = styled("span", {
  shouldForwardProp: (prop) => prop !== "departed",
})<{ departed: boolean }>(({ theme, departed }) => ({
  color: theme.palette.text.disabled,
  display: "block",
  minWidth: 0,
  width: "100%",
  ...(departed && {
    "&, & *": {
      textDecorationColor: removedRowRedlineColour(theme, RemovedRowRedlineTone.Settled),
      textDecorationLine: "line-through",
      textDecorationThickness: "2px",
    },
  }),
}));
const Root = styled("div")({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
  minWidth: 0,
  overflow: "hidden",
  position: "relative",
});
const LOCKED_COLUMN_WIDTH = 40;
/**
 * How wide a category description is allowed to get before it is cut off.
 *
 * Descriptions are free text and routinely run to a paragraph; left unbounded they pushed the fee and
 * billing code off the visible row entirely (#4490). The full text is one hover away — the cell
 * measures its own truncation and only then offers the tooltip.
 */
const DESCRIPTION_MAX_WIDTH = 280;

/**
 * The floor under each of this grid's headings, which still draw a sort arrow and a column menu beside
 * their labels. The shared fee schedule grid's own floors dropped to its labels when it moved those
 * controls onto hover (#6545); until this grid does the same, it keeps the floors it had, and every
 * other column takes the shared one.
 */
const HEADING_WITH_TOOLS_MINIMUMS: Readonly<Record<string, number>> = {
  name: 150,
  description: 160,
  fee: 120,
  teamMember: 170,
  type: 95,
};

function headingMinimum(columnId: string): number {
  return HEADING_WITH_TOOLS_MINIMUMS[columnId] ?? feeScheduleColumnWidth(columnId).minimum;
}

/** What the schedule's add control calls its two ways of gaining a category. */
export const ADD_NEW_CATEGORY_LABEL = "Add New Category";
export const ADD_EXISTING_CATEGORY_LABEL = "Add Existing Category";

const ClampedDescription = styled("div")(() => ({
  minWidth: 0,
  width: "100%",
  // The cap is on the description itself as well as on the cell around it (#5405), so a table that
  // shares its spare width out to this column still draws the description at its natural 280px.
  [`& .${redlineCellClasses.value}`]: {
    maxWidth: DESCRIPTION_MAX_WIDTH,
  },
  // Stated on the wrapper as well as on the text inside it. The wrapper is what a reader's pointer
  // is over, and what the measured tooltip below asks whether the text is cut off in.
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  "& .MuiInputBase-input": {
    overflow: "hidden",
  },
  // ONE line, with an ellipsis, exactly as every other cell in these tables reads. It used to be a
  // two-line `-webkit-box` clamp, which is the multi-line wrapping #4955's review asked to be taken
  // off the collection tables: a row whose height depends on how much someone wrote is a row that
  // cannot be scanned against its neighbours. The whole description is still one hover away, and
  // the column can be dragged wider or double-clicked open (#4955).
  "& .MuiInputBase-input > div, & .MuiTypography-root": {
    lineHeight: "inherit",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));

function ClampedDescriptionField({
  children,
  title,
}: Readonly<{ children: ReactNode; title: string }>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const updateTooltip = () => {
    const root = rootRef.current;
    const clippedElements = root
      ? [
        root,
        ...root.querySelectorAll<HTMLElement>(
          ".MuiInputBase-input > div, .MuiTypography-root, .MuiInputBase-input",
        ),
      ]
      : [];
    setTooltipOpen(Boolean(
      title && clippedElements.some((element) =>
        element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight)
    ));
  };

  return (
    <Tooltip
      open={tooltipOpen}
      title={title}
      placement="top-start"
      onClose={() => setTooltipOpen(false)}
      disableFocusListener
      disableTouchListener
      slotProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}
    >
      <ClampedDescription
        ref={rootRef}
        onMouseEnter={updateTooltip}
        onMouseLeave={() => setTooltipOpen(false)}
      >
        {children}
      </ClampedDescription>
    </Tooltip>
  );
}

/**
 * What every text column's cell passes the grid's text redline: one line of a table cell, drawn and
 * not edited. Nothing is edited here, so a change handler is never called; the component asks for one.
 */
const readOnlyTextRedlineCellProps = {
  cellLayout: true,
  variant: "standard",
  size: "small",
  margin: "none",
  hideLabel: true,
  showPlaceholder: true,
  readOnly: true,
  hideUndoButton: true,
  hideAcceptRejectButtons: true,
  hideFieldHistory: true,
  onTextFieldRedlineChange: () => undefined,
} as const;
// #endregion

// #region Types/Props
type FeeScheduleRedlineSelectionProps = {
  className?: string;
  feeScheduleRedline: FeeScheduleRedline;
  disableEditing?: boolean;
  fieldLockTooltip?: string;
  negotiationId?: string;
  viewerScope?: string;
  activeReviewField: ProposalField | undefined;
  /**
   * Hears the categories in the order the table draws them, sorted and filtered as the reader left it,
   * so a traverser walks the rows in that order (#6617). Undefined once the table is gone.
   */
  onDisplayedCategoriesChange?: (categoryIds: readonly Guid[] | undefined) => void;
  onCategoryClicked?: (categoryId: Guid) => void;
  /**
   * Opens the category editor on a row, with the category as the row shows it. Without it the rows
   * carry no Edit action. See "The exception" in docs/software-design/table-row-actions.md (#5396).
   */
  onEditCategoryClicked?: (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => void;
  onCreateCategoryClicked: () => void;
  onFeeScheduleRedlineChange: (
    newRedline: FeeScheduleRedline,
    traversalFieldOverride?: ProposalField | null
  ) => void;
  onExistingCategoryClicked: () => void;
  /**
   * Save as Template, from the proposal tab that holds this schedule. Its own button beside the add
   * control, not an entry in its dropdown, so a reader sees it without opening a menu (#5808).
   */
  saveAsTemplateAction?: TableAddAction;
  /**
   * The vendor team as the redline has it now. Given, a rate naming somebody it does not have is
   * flagged in the Team Member column (#5798).
   */
  teamRoster?: FeeScheduleTeamRoster;
  /**
   * The Team Member issue on each category that has one, by category id. Given, that category's Team
   * Member cell is coloured at the issue's level, with the issue's words in its tooltip (#6097).
   */
  teamMemberIssues?: ReadonlyMap<string, ProposalIssue>;
  /**
   * The Fee issue on each category that has one, a fee deferred to the other side and not set yet,
   * by category id. Given, that category's Fee cell is coloured as a Team Member cell is (#6097).
   */
  feeIssues?: ReadonlyMap<string, ProposalIssue>;
  /**
   * Who has left the vendor team of the work this proposal replaces. A rate still naming one of them,
   * whom `teamRoster` does not have back, reads as departed rather than flagged: its row struck
   * through, and how they left beside the name (#6096).
   */
  teamMemberDepartures?: readonly FeeScheduleTeamMemberDeparture[];
};
// #endregion

const FeeScheduleRedlineSelection = (props: Readonly<FeeScheduleRedlineSelectionProps>) => {
  const {
    className,
    feeScheduleRedline,
    negotiationId,
    viewerScope,
    disableEditing,
    fieldLockTooltip,
    activeReviewField,
    onDisplayedCategoriesChange,
    onCategoryClicked,
    onEditCategoryClicked,
    onCreateCategoryClicked,
    onExistingCategoryClicked,
    onFeeScheduleRedlineChange,
    saveAsTemplateAction,
    teamRoster,
    teamMemberIssues,
    feeIssues,
    teamMemberDepartures,
  } = props;

  const theme = useTheme();
  // The table drops columns by how much room the pane has, not how wide the window is (#4964).
  const isMediumDisplaySize = useWorkPaneIsCompact();
  // On a phone the row's controls fold into one menu, as every list page's do. The viewport, not the
  // pane: a pane narrowed by the comments drawer on a desktop keeps its icons and scrolls (#5907).
  const phoneQuery = theme.breakpoints.down("sm");
  const isPhone = useMediaQuery(phoneQuery);
  const [query, setQuery] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });

  // #region Removed categories
  // The categories the schedule no longer has, kept as struck-through rows of their own so each keeps
  // the history of the category it was (#4951). They are not part of `feeScheduleRedline` — nothing
  // here edits, resolves or undoes them — so every control that writes to the redline looks them up by
  // id and steps aside. A category the redline itself still holds as a removal (open for undo, or
  // awaiting a decision) is already a row, and is not repeated.
  const presentCategoryIds = useMemo(
    () => feeScheduleRedline.redlines.flatMap((redline) => (redline.field.id ? [redline.field.id.value] : [])),
    [feeScheduleRedline],
  );
  const removedCategories = useRemovedProposalFeeScheduleCategories({
    negotiationId,
    viewerScope,
    presentItemIds: presentCategoryIds,
  });
  const removedCategoryRows = useMemo(
    () =>
      removedCategories.map(
        // Original present, revised and current absent: the shape of an accepted removal, which the cells
        // already draw struck through.
        (removed) => new FeeScheduleCategoryRedline(removed.category.id, removed.category, null, null),
      ),
    [removedCategories],
  );
  const removedCategoryIds = useMemo(
    () => new Set(removedCategoryRows.flatMap((row) => (row.field.id ? [row.field.id.value] : []))),
    [removedCategoryRows],
  );
  const isRemovedCategoryRow = useCallback(
    (row: FeeScheduleCategoryRedline) => Boolean(row.field.id && removedCategoryIds.has(row.field.id.value)),
    [removedCategoryIds],
  );
  const rows = useMemo(
    () => [...feeScheduleRedline.redlines, ...removedCategoryRows],
    [feeScheduleRedline.redlines, removedCategoryRows],
  );
  // #endregion

  // #region Refs
  // The container the table scrolls in. The scrollbar on it is `MRTNoMemo`'s, and it is what says the
  // rows continue past the right-hand edge — the shaded edges and arrows that used to say so are gone
  // (#4955).
  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  // #region Functions
  const handleRemoveClicked = useCallback(
    (categoryId: Guid) => {
      const newRedline = feeScheduleRedline.removeEntryByFieldId(categoryId);
      onFeeScheduleRedlineChange(newRedline, newRedline.field);
    },
    [feeScheduleRedline, onFeeScheduleRedlineChange],
  );

  // A revert restores a prior category value through the SAME `replaceEntryById` edit path the category
  // editor's save reaches through `reviseEntryById`, so it records an ordinary redline "Edit" and
  // flows to `queueProposalUpdate` as a `CollectionItemUpdated`/`CollectionItemAdded` op — never a raw
  // `FieldSet` (see proposal-collection-item-revert.ts). Because it lands as an Edit in the redline's
  // session history exactly like a manual edit would, the save-time resolution-decision builder
  // (proposal-resolution-decision-builder.ts) classifies it as `AcceptIncoming`/`KeepPrevious`/
  // `ProposeAlternative` from the *resulting* materialized value automatically — no separate handling
  // is needed here to satisfy the "revert must be a resolution decision" rule.
  const handleCategoryRevert = useCallback(
    (categoryFieldId: Guid, entry: ProposalFieldTimelineRevertEntry) => {
      // This dropdown instance is always in collection-item mode (a fee-schedule category), so
      // `entry.operation` is always CollectionItemAdded/Updated at runtime — narrowed here to satisfy
      // `proposalCollectionItemRevertValue`'s parameter type, which excludes `FieldSet`.
      if (entry.operation?.kind !== "CollectionItemAdded" && entry.operation?.kind !== "CollectionItemUpdated") return;
      const revertValue = proposalCollectionItemRevertValue(entry.operation);
      if (revertValue === undefined) return;
      // A single-column revert (#4847) restores one property and must leave the rest of the category
      // alone — including any column the counterparty has since changed. So it is merged onto the
      // category as it currently stands rather than replacing it; a whole-entry revert still replaces.
      const revertedAspect = proposalCollectionItemRevertAspect(entry.operation);
      const currentCategory = feeScheduleRedline.redlines.find((redline) =>
        redline.field.id?.isEqualTo(categoryFieldId),
      );
      const currentJSON = (currentCategory?.currentEntry ?? currentCategory?.revisedEntry)?.toJSON();
      // One column on its own is not a category, so without the category to merge it into there is
      // nothing to restore — building one from the fragment would fail schema validation inside a click.
      if (revertedAspect && !currentJSON) return;
      const revertedJSON = revertedAspect
        ? { ...currentJSON, ...(revertValue as Partial<NonNullable<typeof currentJSON>>) }
        : revertValue;
      const revertedCategory = FeeScheduleCategory.fromJSON(revertedJSON as never);
      if (!revertedCategory) return;
      const newRedline = feeScheduleRedline.replaceEntryById(categoryFieldId, revertedCategory);
      onFeeScheduleRedlineChange(newRedline, ProposalField.FeeScheduleCategory(categoryFieldId));
    },
    [feeScheduleRedline, onFeeScheduleRedlineChange],
  );

  /**
   * Talks the schedule back into a category it no longer has.
   *
   * A tombstone is outside `feeScheduleRedline` — its id is absent from the collection, which is the
   * whole reason it is a tombstone — so the way back is `addEntry`, not `replaceEntryById`, which
   * throws on an id it cannot find. `addEntry` takes the same edit path any other category addition
   * uses, so the restore diffs to a `CollectionItemAdded` through `buildCollectionOperations` with no
   * wire work of its own, and never a raw `FieldSet` — the same rule a revert follows, and for the
   * same reason (`proposal-collection-item-revert.ts`: when the item's most recent event was its
   * removal there is nothing to update, and restoring it is an add).
   *
   * The row then evicts itself: the restored id joins `presentCategoryIds`, so the removed-categories
   * reading stops listing it and the live redline row takes its place.
   */
  const handleRestoreClicked = useCallback(
    (category: FeeScheduleCategoryEntry) => {
      if (!category) return;
      onFeeScheduleRedlineChange(feeScheduleRedline.addEntry(category), ProposalField.FeeScheduleCategory(category.id));
    },
    [feeScheduleRedline, onFeeScheduleRedlineChange],
  );

  /**
   * #5455: `manualPagination` was `true` with no pagination state, so the pager always read a table
   * stuck on page one of ten regardless of how many categories the reader had. The rows are a handful
   * of categories held in memory, so MRT pages them here — the way `readonly-fee-schedule-categories-
   * table` already does. A filter or a sort that leaves fewer rows than the page the reader was on
   * left them looking at an empty page there; jumping back to the first page on either change is what
   * that table already does too.
   */
  const resetToFirstPage = useCallback(() => {
    setPagination((previous) => ({ ...previous, pageIndex: 0 }));
  }, []);

  const handleColumnFiltersChange = useCallback((updater: MRT_Updater<ColumnFiltersState>) => {
    resetToFirstPage();
    setColumnFilters(updater);
  }, [resetToFirstPage]);

  const handleGlobalFilterChange = useCallback((updater: MRT_Updater<string>) => {
    resetToFirstPage();
    setQuery(updater);
  }, [resetToFirstPage]);

  const handleSortingChange = useCallback((updater: MRT_Updater<SortingState>) => {
    resetToFirstPage();
    setSorting(updater);
  }, [resetToFirstPage]);

  // #endregion

  // #region Cells
  // Every cell is the redline of one column, drawn and never edited: the category is changed from its
  // row's Edit action, in the panel, which draws these same redlines beside its fields (#5570). A cell
  // used to be an editor of its own, and opening one hid every other column ("focus mode") to make the
  // room the editor needed — so a category had two ways to be changed that did not show the same thing.
  const renderTypeRedline = useCallback((rowData: FeeScheduleCategoryRedline) => {
    if (!rowData.field.id) {
      console.error("Category Redline ID is malformed", rowData);
      return;
    }
    const categoryRedline = Object.assign(new FeeScheduleCategoryRedline(rowData.field.id, null, null), rowData);
    if (!categoryRedline.typeRedline) {
      console.error("Category Redline type is malformed", categoryRedline);
      return;
    }
    return (
      <TextRedlineInput
        {...readOnlyTextRedlineCellProps}
        textRedline={categoryRedline.typeRedline}
        fieldConstructor={FeeScheduleCategoryTypeText}
      />
    );
  }, []);

  // #region Description Cell
  const renderDescriptionRedline = useCallback((rowData: FeeScheduleCategoryRedline) => {
    if (!rowData.field.id) {
      console.error("Category Redline ID is malformed", rowData);
      return;
    }
    const categoryRedline = Object.assign(new FeeScheduleCategoryRedline(rowData.field.id, null, null), rowData);
    if (!categoryRedline.descriptionRedline) {
      console.error("Category Redline description is malformed", categoryRedline);
      return;
    }
    return (
      <ClampedDescriptionField title={fullDescriptionText(categoryRedline)}>
        <TextRedlineInput
          {...readOnlyTextRedlineCellProps}
          textRedline={categoryRedline.descriptionRedline}
          fieldConstructor={FeeScheduleCategoryDescription}
        />
      </ClampedDescriptionField>
    );
  }, []);

  // #region Fee Cell
  const renderFeeRedline = useCallback((rowData: FeeScheduleCategoryRedline) => {
    if (
      rowData.originalEntry instanceof FeeScheduleCategoryMaskedCurrency ||
      rowData.revisedEntry instanceof FeeScheduleCategoryMaskedCurrency
    ) {
      return <em>Masked</em>;
    }
    if (!rowData.field.id) {
      console.error("Category Redline ID is malformed", rowData);
      return;
    }
    const categoryRedline = Object.assign(new FeeScheduleCategoryRedline(rowData.field.id, null, null), rowData);
    if (!categoryRedline.feeRedline) {
      console.error("Category Redline fee is malformed", categoryRedline);
      return;
    }

    const feeRedline = (
      <FeeRedlineInput
        cellLayout
        variant="standard"
        size="small"
        margin="none"
        categoryRedline={categoryRedline}
        readOnly
      />
    );
    const issue = categoryRedline.isRemoved ? undefined : feeIssues?.get(rowData.field.id.value);
    if (!issue) return feeRedline;
    return (
      <FeeScheduleIssueFlag
        reason={issue.description}
        severity={issueFlagSeverity(issue)}
        cellKey={feeScheduleIssueCellKey(rowData.field.id.value, ProposalIssuePart.Fee)}
      >
        {feeRedline}
      </FeeScheduleIssueFlag>
    );
  }, [feeIssues]);

  // #region Name Cell
  const renderNameRedline = useCallback((rowData: FeeScheduleCategoryRedline) => {
    if (!rowData.field.id) {
      console.error("Category Redline ID is malformed", rowData);
      return;
    }
    const categoryRedline = Object.assign(new FeeScheduleCategoryRedline(rowData.field.id, null, null), rowData);
    if (!categoryRedline.nameRedline) {
      console.error("Category Redline Name is malformed", categoryRedline);
      return;
    }
    return (
      <TextRedlineInput
        {...readOnlyTextRedlineCellProps}
        id={categoryRedline.field.id?.value}
        bold={true}
        textRedline={categoryRedline.nameRedline}
        fieldConstructor={FeeScheduleCategoryName}
      />
    );
  }, []);

  // #region Columns
  const columnHelper = useMemo(() => createMRTColumnHelper<FeeScheduleCategoryRedline>(), []);
  const teamMemberUserIds = useMemo(
    () => new Set(
      // Every entry's member, not only the one the row shows: a changed member's redline names the
      // member it replaced too (#6632).
      rows.flatMap((redline) =>
        [redline.originalEntry, redline.revisedEntry, redline.currentEntry].flatMap((category) =>
          category?.teamMember && !category.teamMemberIsMasked ? [category.teamMember.userId.value] : [],
        ),
      ),
    ),
    [rows],
  );
  const teamMemberIndividualsQuery = useGetIndividualsByUserIdsQuery(
    teamMemberUserIds.size > 0 ? { userIds: [...teamMemberUserIds] } : skipToken,
  );
  const teamMemberNames = useMemo(
    () => new Map(
      (Individual.fromJSONArray(teamMemberIndividualsQuery.data, { allowEmpty: true }) ?? []).map((individual) => [
        individual.userId.value,
        individual.fullName,
      ]),
    ),
    [teamMemberIndividualsQuery.data],
  );
  // The fee schedule table's own words for the three states, from the module every other fee schedule
  // table reads them from (#5251), rather than three more string literals here.
  const teamMemberText = useCallback(
    (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => {
      if (category.teamMemberIsMasked) return proposalFeeScheduleRestrictedTeamMemberText;
      if (!category.teamMember) return proposalFeeScheduleUnassignedText;
      return teamMemberNames.get(category.teamMember.userId.value) ?? proposalFeeScheduleTeamMemberFallbackText;
    },
    [teamMemberNames],
  );
  const departuresByUserId = useMemo(() => indexDeparturesByUserId(teamMemberDepartures), [teamMemberDepartures]);
  /** How the rate a row shows reads as departed, when it does. A removed category is a record, not a rate. */
  const rowDeparture = useCallback(
    (rowData: FeeScheduleCategoryRedline) => {
      if (isRemovedCategoryRow(rowData)) return undefined;
      const category = displayedCategory(rowData);
      if (!category || rowData.isRemoved) return undefined;
      return departureOfRate(category, departuresByUserId, teamRoster?.isOffTeam(category.teamMember) ?? true);
    },
    [departuresByUserId, isRemovedCategoryRow, teamRoster],
  );
  const renderTeamMember = useCallback(
    (rowData: FeeScheduleCategoryRedline, table: MRT_TableInstance<FeeScheduleCategoryRedline>) => {
      if (!rowData.field.id) return null;
      const categoryRedline = Object.assign(new FeeScheduleCategoryRedline(rowData.field.id, null, null), rowData);
      const category = displayedCategory(categoryRedline);
      if (!category) return null;

      const displayName = teamMemberText(category);
      // A changed member is drawn as the other columns draw a change: the old name struck, the new one
      // marked (#6632). A removed category's member is struck with the rest of its row instead.
      const teamMemberRedline = categoryRedline.isRemoved
        ? undefined
        : feeScheduleTeamMemberRedline(categoryRedline, teamMemberText);
      const nameDisplay = teamMemberRedline
        ? <TextRedlineInput {...readOnlyTextRedlineCellProps} textRedline={teamMemberRedline} fieldConstructor={Text} />
        : (
          <FeeScheduleTableCellText title={displayName}>
            <SearchMatch text={displayName} table={table} />
          </FeeScheduleTableCellText>
        );
      const issue = categoryRedline.isRemoved ? undefined : teamMemberIssues?.get(rowData.field.id.value);
      const isOffTeam =
        !categoryRedline.isRemoved && !category.teamMemberIsMasked && (teamRoster?.isOffTeam(category.teamMember) ?? false);
      const departure = rowDeparture(categoryRedline);
      // A removed category reads as removed, not departed, but a member who left the team keeps the
      // settled line they were struck with when they left.
      const removedMemberDeparted = categoryRedline.isRemoved && !categoryRedline.currentEntry && Boolean(
        departureOfRate(category, departuresByUserId, teamRoster?.isOffTeam(category.teamMember) ?? true),
      );
      // A departed member is off the team too, and the departure says more than the flag does (#6096).
      // Otherwise the tab's issue about the rate says the most, in its own words (#6097).
      let teamMemberValueDisplay = nameDisplay;
      if (departure) {
        teamMemberValueDisplay = (
          <FeeScheduleDepartedTeamMember name={displayName} departure={departure}>
            <SearchMatch text={displayName} table={table} />
          </FeeScheduleDepartedTeamMember>
        );
      } else if (issue) {
        teamMemberValueDisplay = (
          <FeeScheduleIssueFlag
            reason={issue.description}
            severity={issueFlagSeverity(issue)}
            cellKey={feeScheduleIssueCellKey(rowData.field.id.value, ProposalIssuePart.TeamMember)}
          >
            {nameDisplay}
          </FeeScheduleIssueFlag>
        );
      } else if (isOffTeam) {
        teamMemberValueDisplay = (
          <FeeScheduleIssueFlag reason={feeScheduleOffTeamFlagText}>{nameDisplay}</FeeScheduleIssueFlag>
        );
      }
      // The shape the other four cells take too (`redline-cell.tsx`): the member's name in the flexible
      // part of the cell (#5405). A removed category's member goes with it: struck through like the rest
      // of the row.
      return (
        <RedlineCell>
          {categoryRedline.isRemoved
            ? (
              <RemovedTeamMember departed={removedMemberDeparted} {...ownStrikeAttribute(removedMemberDeparted)}>
                {teamMemberValueDisplay}
              </RemovedTeamMember>
            )
            : <RedlineCellValue>{teamMemberValueDisplay}</RedlineCellValue>}
        </RedlineCell>
      );
    },
    [departuresByUserId, rowDeparture, teamMemberIssues, teamMemberText, teamRoster],
  );
  const columns = useMemo(
    () => [
      ...(disableEditing && fieldLockTooltip
        ? [
          columnHelper.display({
            header: "",
            id: "locked",
            size: LOCKED_COLUMN_WIDTH,
            enableColumnActions: false,
            enableSorting: false,
            muiTableBodyCellProps: {
              align: "center",
              sx: {
                verticalAlign: "middle",
              },
            },
            Cell: () => (
              <LockedCellContent>
                <ReviewerEditLockIcon title={fieldLockTooltip} />
              </LockedCellContent>
            ),
          }),
        ]
        : []),
      columnHelper.accessor(getCategoryRedlineNameValue, {
        header: "Name",
        id: "name",
        enableGlobalFilter: true,
        filterFn: "includesString",
        Cell: ({ row }) => renderNameRedline(row.original),
      }),
      columnHelper.accessor(getCategoryRedlineDescriptionValue, {
        header: "Description",
        id: "description",
        enableSorting: false,
        enableGlobalFilter: true,
        filterFn: "includesString",
        Cell: ({ row }) => renderDescriptionRedline(row.original),
      }),
      // Fee or Expense, read with the name and description it qualifies (#6882).
      columnHelper.accessor(getCategoryRedlineTypeValue, {
        header: "Type",
        id: "type",
        enableSorting: false,
        enableGlobalFilter: true,
        filterFn: "includesString",
        Cell: ({ row }) => renderTypeRedline(row.original),
      }),
      columnHelper.accessor(getCategoryRedlineFeeValue, {
        header: "Fee",
        id: "fee",
        enableGlobalFilter: true,
        filterFn: "includesString",
        Cell: ({ row }) => renderFeeRedline(row.original),
      }),
      columnHelper.accessor(
        (redline) => {
          const category = displayedCategory(redline);
          return category ? teamMemberText(category) : proposalFeeScheduleUnassignedText;
        },
        {
          header: "Team Member",
          id: "teamMember",
          enableGlobalFilter: true,
          filterFn: "includesString",
          Cell: ({ row, table }) => renderTeamMember(row.original, table),
        },
      ),
    ],
    [
      columnHelper,
      disableEditing,
      fieldLockTooltip,
      renderDescriptionRedline,
      renderFeeRedline,
      renderNameRedline,
      renderTeamMember,
      renderTypeRedline,
      teamMemberText,
    ],
  );
  // The box's left and right edges go on the first and last redlineable columns as they are drawn,
  // read from the definitions above. A separate list put the right edge on Type once #6914 moved it
  // to the middle, and drew it between Type and Fee (#7076).
  const redlineableColumnIds = useMemo(
    () => columns.flatMap((column) => (column.id && REDLINEABLE_COLUMN_IDS.has(column.id) ? [column.id] : [])),
    [columns],
  );

  // #region Row Actions
  const renderRowActions = useCallback(
    (props: { row: MRT_Row<FeeScheduleCategoryRedline> }) => {
      const { row } = props;
      if (!row.original.field.id) return null;

      const category = Object.assign(new FeeScheduleCategoryRedline(row.original.field.id, null, null), row.original);

      if (!category) return null;
      // A row nobody may write to — the viewer is locked out, or the category is one the schedule no
      // longer has — carries the category's own history. Scoped to the category: the schedule as a
      // whole has no history control of its own any more (#4951).
      //
      // A removed category also carries a way back. Its departure is a fact of the negotiation, so the
      // row is a record rather than a decision and nothing here accepts, rejects or undoes it — but a
      // record the schedule can be talked back into is different from a dead end, and restoring is the
      // only write a tombstone offers. A viewer who may not edit at all gets the history alone.
      const isRemovedCategory = isRemovedCategoryRow(category);
      if (disableEditing || isRemovedCategory) {
        if (isPhone) {
          const restoreActions: RowAction[] =
            isRemovedCategory && !disableEditing
              ? [
                {
                  key: "restore",
                  label: "Restore Fee",
                  icon: <RestoreFromTrashIcon color="secondary" />,
                  onClick: () => category.originalEntry && handleRestoreClicked(category.originalEntry),
                },
              ]
              : [];
          return (
            <RowActions
              collapsed
              ariaLabel="Category actions"
              actions={restoreActions}
              trailing={
                <ProposalFieldHistoryButton
                  field={category.field}
                  collectionItemId={row.original.field.id.value}
                  negotiationId={negotiationId}
                  viewerScope={viewerScope}
                />
              }
            />
          );
        }
        return (
          <RowActionsContainer>
            {isRemovedCategory && !disableEditing && (
              <Tooltip title="Restore Fee">
                <span>
                  <ActionButton
                    aria-label="Restore Fee"
                    onClick={() => category.originalEntry && handleRestoreClicked(category.originalEntry)}
                  >
                    <RestoreFromTrashIcon color="secondary" />
                  </ActionButton>
                </span>
              </Tooltip>
            )}
            <ProposalFieldHistoryButton
              field={category.field}
              collectionItemId={row.original.field.id.value}
              negotiationId={negotiationId}
              viewerScope={viewerScope}
            />
          </RowActionsContainer>
        );
      }

      // What the editor opens on: this party's entry, or else the one the counterparty asked for. A
      // removed row has no value to edit.
      const editableEntry = category.isRemoved ? null : (category.currentEntry ?? category.revisedEntry);

      if (isPhone) {
        const actions: RowAction[] = [];
        if (onEditCategoryClicked && editableEntry) {
          actions.push({
            key: "edit",
            label: "Edit Category",
            ariaLabel: `Edit category: ${editableEntry.name.value}`,
            icon: <EditIcon />,
            onClick: () => onEditCategoryClicked(editableEntry),
          });
        }
        if (category.isResolved && !category.isRemoved && !category.isNewlyAdded) {
          actions.push({
            key: "remove",
            label: "Remove Fee",
            icon: <DeleteOutlinedIcon />,
            destructive: true,
            onClick: () => category.field.id && handleRemoveClicked(category.field.id),
          });
        }
        if (!category.isResolved) {
          actions.push(
            {
              key: "accept",
              label: "Accept Changes",
              icon: <CheckIcon color="success" />,
              onClick: () => onFeeScheduleRedlineChange(feeScheduleRedline.updateRedline(category.accept())),
            },
            {
              key: "reject",
              label: "Reject Changes",
              icon: <CloseIcon color="error" />,
              onClick: () => onFeeScheduleRedlineChange(feeScheduleRedline.updateRedline(category.reject())),
            },
          );
        }
        if (category.canBeUndone) {
          actions.push({
            key: "undo",
            label: "Undo Changes",
            icon: <UndoIcon color="secondary" />,
            onClick: () => {
              if (category.field.id) {
                onFeeScheduleRedlineChange(feeScheduleRedline.undoRedlineById(category.field.id), category.field);
              }
            },
          });
        }
        return (
          <RowActions
            collapsed
            ariaLabel={`Actions for ${displayedCategory(category)?.name.value ?? "category"}`}
            actions={actions}
            trailing={
              <ProposalFieldTimelineDropdown
                field={category.field}
                negotiationId={negotiationId}
                viewerScope={viewerScope}
                toolTip="Column change history"
                collectionItemAspects={proposalFeeScheduleAspectOptions}
                onRevert={(entry) => category.field.id && handleCategoryRevert(category.field.id, entry)}
              />
            }
          />
        );
      }

      return (
        <RowActionsContainer>
          {onEditCategoryClicked && editableEntry && (
            <Tooltip title="Edit Category">
              <span>
                <ActionButton
                  aria-label={`Edit category: ${editableEntry.name.value}`}
                  onClick={() => onEditCategoryClicked(editableEntry)}
                >
                  {/* Neutral, as the row's other controls are: the trash and the history beside it. */}
                  <EditIcon />
                </ActionButton>
              </span>
            </Tooltip>
          )}
          {category.isResolved && !category.isRemoved && !category.isNewlyAdded && (
            <Tooltip title="Remove Fee">
              <span>
                <ProposalDeleteActionButton
                  aria-label="Remove Fee"
                  onClick={() => category.field.id && handleRemoveClicked(category.field.id)}
                />
              </span>
            </Tooltip>
          )}
          {!category.isResolved && (
            <>
              <Tooltip title="Accept Changes">
                <span>
                  <ActionButton
                    onClick={() => {
                      onFeeScheduleRedlineChange(feeScheduleRedline.updateRedline(category.accept()));
                    }}
                  >
                    <CheckIcon color="success" />
                  </ActionButton>
                </span>
              </Tooltip>
              <Tooltip title="Reject Changes">
                <span>
                  <ActionButton
                    onClick={() => {
                      onFeeScheduleRedlineChange(feeScheduleRedline.updateRedline(category.reject()));
                    }}
                  >
                    <CloseIcon color="error" />
                  </ActionButton>
                </span>
              </Tooltip>
            </>
          )}
          {category.canBeUndone && (
            <Tooltip title="Undo Changes">
              <span>
                <ActionButton
                  onClick={() => {
                    if (category.field.id) {
                      onFeeScheduleRedlineChange(feeScheduleRedline.undoRedlineById(category.field.id), category.field);
                    }
                  }}
                >
                  <UndoIcon color="secondary" />
                </ActionButton>
              </span>
            </Tooltip>
          )}
          <ProposalFieldTimelineDropdown
            field={category.field}
            negotiationId={negotiationId}
            viewerScope={viewerScope}
            toolTip="Column change history"
            // A category is negotiated column by column, so its history is browsed and reverted column
            // by column too (#4847) — with the category's own arrivals and departures kept in their own
            // section beneath, which is the whole-schedule tracking the issue asks to keep.
            collectionItemAspects={proposalFeeScheduleAspectOptions}
            onRevert={(entry) => category.field.id && handleCategoryRevert(category.field.id, entry)}
          />
        </RowActionsContainer>
      );
    },
    [
      disableEditing,
      isPhone,
      isRemovedCategoryRow,
      negotiationId,
      viewerScope,
      handleRemoveClicked,
      handleCategoryRevert,
      handleRestoreClicked,
      onEditCategoryClicked,
      onFeeScheduleRedlineChange,
      feeScheduleRedline,
    ],
  );

  // #region GetRowId
  const getRowId = useCallback((row: FeeScheduleCategoryRedline) => {
    return row.field.id?.value ?? Guid.generate().value;
  }, []);

  /** The row the traverser is on, so the table turns to its page when it lands (#6617). */
  const activeRowId = rows.find((row) => row.field?.isEqualTo(activeReviewField))?.field.id?.value;

  // #region muiTableProps
  const muiTableProps = useCallback(() => {
    return ({
      sx: {
        borderSpacing: 0,
      },
    });
  }, []);

  // #region muiTableBodyRowProps
  const muiTableBodyRowProps = useCallback((props: { row: MRT_Row<FeeScheduleCategoryRedline> }) => {
    const { row } = props;
    const isActiveRow = row.original.field?.isEqualTo(activeReviewField);
    const isRemovedCategory = isRemovedCategoryRow(row.original);
    // A category the schedule dropped in an earlier turn is settled; one the redline still holds as a
    // removal is struck in its own party's colour.
    const removedTone = isRemovedCategory ? RemovedRowRedlineTone.Settled : removedRowRedlineTone(row.original);
    return {
      hover: false,
      onClick: (event: ReactMouseEvent<HTMLTableRowElement>) => {
        if (disableEditing || isRemovedCategory) return;
        if (!row.original.field.id) return;
        const target = event.target;
        if (
          target instanceof Element &&
          target.closest(
            "button, a, input, textarea, select, [role='button'], [contenteditable='true'], .MuiFormControl-root, .MuiInputBase-root",
          )
        ) return;
        onCategoryClicked?.(row.original.field.id);
      },
      // The stop's highlight, and what the page scrolls to — see `traverser-stop-scope`.
      className: traverserStopClassName(isActiveRow),
      "data-departed-rate": rowDeparture(row.original)?.label,
      [REMOVED_ROW_REDLINE_ATTRIBUTE]: removedTone,
      sx: {
        ...(isActiveRow && {
          animation: traverserStopRowBorderPulse,
        }),
        cursor: disableEditing || isRemovedCategory ? "default" : undefined,
        ...(removedTone && removedRowRedlineStyles(removedTone)(theme)),
      },
    }
  }, [activeReviewField, disableEditing, isRemovedCategoryRow, onCategoryClicked, rowDeparture, theme]);

  const muiTableBodyCellProps = useCallback((props: {
    table: MRT_TableInstance<FeeScheduleCategoryRedline>;
    cell: { column: { id: string }; getValue?: () => unknown };
    row: MRT_Row<FeeScheduleCategoryRedline>;
  }) => {
    const { table, cell, row } = props;
    const isRedlineableFeeScheduleColumn = REDLINEABLE_COLUMN_IDS.has(cell.column.id);
    const isActiveRow = row.original.field?.isEqualTo(activeReviewField);
    // The green "resolved" box is for a decision taken in this editor. A category removed earlier in
    // the negotiation has the same shape as an accepted removal, but is a record, not a decision.
    const isResolvedAccepted = (redline: FeeScheduleCategoryRedline | undefined) =>
      redline !== undefined &&
      !isRemovedCategoryRow(redline) &&
      Boolean(redline.wasRedlined) &&
      Boolean(redline.isResolved) &&
      Boolean(redline.isAccepted);
    const showResolvedBackground = isRedlineableFeeScheduleColumn && isResolvedAccepted(row.original);
    const visibleRows = table.getRowModel().rows;
    const visibleRowIndex = visibleRows.findIndex((visibleRow) => visibleRow.id === row.id);
    const previousRow = visibleRows[visibleRowIndex - 1]?.original;
    const nextRow = visibleRows[visibleRowIndex + 1]?.original;
    const previousRowIsResolvedAccepted = isResolvedAccepted(previousRow);
    const nextRowIsResolvedAccepted = isResolvedAccepted(nextRow);
    const isFirstResolvedColumn = cell.column.id === redlineableColumnIds[0];
    const isLastResolvedColumn = cell.column.id === redlineableColumnIds.at(-1);

    const capDescriptionWidth = cell.column.id === "description";

    return {
      sx: {
        ...feeScheduleTableCellStyles(theme, { columnId: cell.column.id }),
        ...(capDescriptionWidth && {
          maxWidth: DESCRIPTION_MAX_WIDTH,
          minWidth: 0,
        }),
        [paneIsCompact(theme)]: {
          padding: theme.spacing(0.5, 1),
          // The pinned controls take 16px less of a narrow pane: 4px either side of them rather than
          // 8, and 6px round each icon rather than 8 (#6041). Two 40px icons in 96px left the column
          // wider than its buttons and laid over more of Description than it needed.
          ...(cell.column.id === ROW_ACTIONS_COLUMN_ID && {
            paddingInline: theme.spacing(0.5),
            "& .MuiIconButton-root": { padding: "6px" },
          }),
          "& .MuiTextField-root": {
            margin: 0,
            minWidth: "6rem",
          },
          "& .MuiInputBase-root": {
            minHeight: 34,
          },
          "& .MuiInputBase-input": {
            paddingBottom: theme.spacing(0.5),
            paddingTop: theme.spacing(0.5),
          },
          "& .MuiFormHelperText-root": {
            marginTop: 0,
          },
        },
        ...(showResolvedBackground && {
          backgroundColor: alpha(green[500], 0.06),
          ...(!previousRowIsResolvedAccepted && {
            borderTop: `1px solid ${green[500]}`,
          }),
          ...(!nextRowIsResolvedAccepted && {
            borderBottom: `1px solid ${green[500]}`,
          }),
          ...(isFirstResolvedColumn && {
            borderLeft: `1px solid ${green[500]}`,
          }),
          ...(isLastResolvedColumn && {
            borderRight: `1px solid ${green[500]}`,
          }),
        }),
        ...(isActiveRow && isRedlineableFeeScheduleColumn && {
          borderTop: "2px solid",
          borderBottom: "2px solid",
          ...(isFirstResolvedColumn && {
            borderLeft: "2px solid",
          }),
          ...(isLastResolvedColumn && {
            borderRight: "2px solid",
          }),
          borderColor: "inherit",
          ...traverserStopCellTint,
        }),
        // The category is still agreed, so it stays editable: the line says nobody on the team works
        // it until it is restaffed (#6096). The Team Member cell draws its own.
        ...(isRedlineableFeeScheduleColumn && cell.column.id !== "teamMember" && rowDeparture(row.original) &&
          departedRateCellStyles(theme)),
      },
    };
  }, [activeReviewField, isRemovedCategoryRow, redlineableColumnIds, rowDeparture, theme]);

  // #region muiTableHeadCellProps
  /**
   * Every column is floored at the width it needs to name itself — the same minimums the shared fee
   * schedule grid is fitted with — so a narrow pane scrolls this table rather than squeezing its
   * headers to "Tea…" (#5395). Above the floors the browser's auto layout shares out the room as it
   * always has. The actions column's width is `MRTNoMemo`'s, which layers over this.
   */
  const muiTableHeadCellProps = useCallback(
    (props: { column: { id: string } }) => ({
      sx: {
        minWidth: headingMinimum(props.column.id),
        ...(props.column.id === "description" && { maxWidth: DESCRIPTION_MAX_WIDTH }),
      },
    }),
    [],
  );

  // #region renderEmptyRowsFallback
  const renderEmptyRowsFallback = useMemo(
    () => emptyRowsFallback<FeeScheduleCategoryRedline>({ subject: "fee schedule categories" }),
    [],
  );

  // #region renderTopToolbarCustomActions
  // The two ways a schedule gains a category are one split button now rather than two full-width
  // buttons stacked above the search: "Add New Category" is the press it makes, "Add Existing Category"
  // the alternative behind its dropdown (#4955).
  const addActions = useMemo(
    () =>
      disableEditing
        ? []
        : [
          { label: ADD_NEW_CATEGORY_LABEL, onClick: onCreateCategoryClicked },
          {
            label: ADD_EXISTING_CATEGORY_LABEL,
            icon: <SnippetFolderIcon fontSize="small" />,
            onClick: onExistingCategoryClicked,
          },
        ],
    [disableEditing, onCreateCategoryClicked, onExistingCategoryClicked],
  );

  // The search first, the add control beside it, and the three view buttons at the far end — one
  // wrapping row, so the add control sits to the RIGHT of the search wherever there is room and drops
  // underneath where there is not. No breakpoint decides it; the row breaks when the width says so.
  //
  // #4953: no schedule-wide Accept All / Reject All / Undo Fee Changes here. A category is decided on
  // its own row, and the whole-proposal bulk actions on the active proposal page cover "settle
  // everything at once" — a second, collection-scoped copy of them was clutter.
  const renderTopToolbarCustomActions = useCallback(
    ({ table }: { table: MRT_TableInstance<FeeScheduleCategoryRedline> }) => (
      <CollectionToolbar>
        <CollectionToolbarSearch>
          <GlobalFilterTextField table={table} />
        </CollectionToolbarSearch>
        <TableAddActions
          actions={addActions}
          standaloneActions={!disableEditing && saveAsTemplateAction ? [saveAsTemplateAction] : undefined}
        />
        <CollectionToolbarViewButtons>
          <TableToolsMenu table={table} />
        </CollectionToolbarViewButtons>
      </CollectionToolbar>
    ),
    [addActions, disableEditing, saveAsTemplateAction],
  );
  // #region renderToolbarInternalActions
  // No whole-schedule history here: a category's history lives on its row, and a removed category keeps
  // a row of its own (#4951). Reading the schedule's history beside the categories' was the confusion.
  //
  // An empty fragment rather than `null`: MRT reads this slot's result with `??`, so a nullish return
  // falls straight back to its own default buttons and the page would carry two sets.
  const renderToolbarInternalActions = useCallback(() => <></>, []);

  // #region muiTableContainerProps
  // `overflowX: auto` is what makes the remaining overflow reachable at all: every ancestor of this
  // table clips, so before it a column pushed past the viewport was simply gone (#4490). The styled
  // scrollbar and the floating scroll buttons `MRTNoMemo` draws make that scrolling evident (#5395).
  const muiTableContainerProps = useMemo(
    () => ({
      ref: tableContainerRef,
      sx: {
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        overflowX: "auto",
        overflowY: "auto",
        position: "relative",
      },
    }),
    [tableContainerRef],
  );

  // #region Table Options
  const tableOptions: MRT_TableOptions<FeeScheduleCategoryRedline> = useMemo(
    () => ({
      // Deliberately NOT `resizableColumnOptions()`, which every other collection table this PR
      // touches now takes. Resizing needs MRT's `grid` layout, and this table's cells are not laid out
      // for it: a category's name, description and fee each carry an inline edit control beside the
      // value, and in a grid column sized to a declared width those controls are pushed out of the
      // cell. Giving them room back means re-deciding what each of these cells IS, which is exactly
      // the restructuring #4955's review put out of scope for the tab it named as the reference. What
      // this table takes from that review is everything that does not touch its columns: the split add
      // control, the toolbar's one wrapping row, the one-line description, and the scrollbar that
      // replaced its chevrons.
      //
      // #5405 gave the five cells one shape (`redline-cell.tsx`), and #5570 took their edit controls
      // away: a category is edited in the panel now, so every cell is a value that truncates in the
      // flexible part and nothing else. The layout is still automatic and no column declares a width;
      // moving this table onto the resizable layout is its own change.
      columns,
      data: rows,
      enableKeyboardShortcuts: false,
      muiPaginationProps: {
        color: "primary",
        shape: "rounded",
        showRowsPerPage: false,
        variant: "outlined",
      },
      enableRowSelection: false,
      enableTableHead: true,
      getRowId,
      initialState: {
        showColumnFilters: false,
        showGlobalFilter: true,
        columnPinning: { right: [ROW_ACTIONS_COLUMN_ID] },
        density: isMediumDisplaySize ? "compact" : "comfortable",
      },
      enableColumnPinning: true,
      enableColumnFilters: true,
      enableGlobalFilter: true,
      enableGlobalFilterRankedResults: false,
      globalFilterFn: "includesString",
      manualFiltering: false,
      enableStickyHeader: true,
      enableStickyFooter: true,
      // #5455: this was `true` with no pagination state at all, so the pager always read a table stuck
      // on page one of ten no matter how many categories the schedule held. `manualPagination` is
      // deliberately absent now, the same way `manualSorting` already was below: the rows are a
      // handful of categories held in memory, so MRT pages them here.
      manualPagination: false,
      // TanStack Table turns back to page 1 whenever the data is new, and the page hands this table new
      // data on every save and every change that arrives. A row the traverser had just turned to page 2
      // for went back to page 1 a moment later (#6617). A search, a filter or a sort still returns to
      // page 1 through its own handler, and Material React Table pulls a page that has emptied back in range.
      autoResetPageIndex: false,
      // #4955: the sort is REAL, and `manualSorting` is deliberately absent. It used to be `true`, which
      // tells MRT the rows arrive already ordered and to leave them where they are — while nothing here
      // ever ordered `rows` by the `sorting` state. So every `⋮ → Sort by …` item moved the header
      // arrow and changed nothing, which is worse than offering no sort at all. Client-side sorting on
      // the accessor values is what those menu items already promise, and a schedule is a handful of
      // rows, so MRT sorts them here.
      enableRowActions: true,
      onColumnFiltersChange: handleColumnFiltersChange,
      onGlobalFilterChange: handleGlobalFilterChange,
      onSortingChange: handleSortingChange,
      onPaginationChange: setPagination,
      state: {
        columnFilters,
        globalFilter: query,
        pagination,
        sorting,
      },
      muiTableProps,
      muiTableBodyRowProps,
      muiTableBodyCellProps,
      muiTableHeadCellProps,
      renderEmptyRowsFallback,
      renderTopToolbarCustomActions,
      renderToolbarInternalActions,
      renderRowActions: renderRowActions,
      muiTableContainerProps,
      positionGlobalFilter: "none",
      muiTopToolbarProps: {
        sx: {
          // A zero floor on the inner Box is what lets MRT's toolbar grid track clamp to the pane
          // rather than size itself to its content — without it the toolbar, which is
          // `overflow: hidden`, clips whatever runs past the right edge and the one wrapping row
          // inside it never gets the chance to wrap.
          "& > .MuiBox-root": {
            [paneIsCompact(theme)]: {
              flexDirection: "column",
              gap: 0,
              marginLeft: 0,
              padding: 0,
            },
            alignItems: "center",
            flexDirection: "row",
            // MRT's own 8px padding is the whole inset, which is the inset the tab's issue notices
            // above stand in; another 8px here put the search 8px inside the notice's left edge.
            minWidth: 0,
          },
        },
      },
      muiTablePaperProps: {
        ...paneStretchTable,
        elevation: 0,
        sx: {
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
        },
      },
      muiTableBodyProps: {
        sx: {
          height: "100%",
          "& tr:only-of-type td[colspan]": {
            height: "100%",
            verticalAlign: "middle",
            padding: 0,
            borderBottom: "none",
          },
        },
      },
      muiSearchTextFieldProps: {
        placeholder: "Search",
        slotProps: {
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Clear search"
                  size="small"
                  onClick={() => setQuery("")}
                >
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          },
        },
      },
    }),
    [
      theme,
      columns,
      rows,
      columnFilters,
      query,
      pagination,
      sorting,
      isMediumDisplaySize,
      getRowId,
      muiTableProps,
      muiTableBodyRowProps,
      muiTableBodyCellProps,
      muiTableHeadCellProps,
      renderEmptyRowsFallback,
      renderTopToolbarCustomActions,
      renderToolbarInternalActions,
      renderRowActions,
      muiTableContainerProps,
      handleColumnFiltersChange,
      handleGlobalFilterChange,
      handleSortingChange,
    ]
  );

  // #region Render
  return (
    <Root className={className}>
      <MRTNoMemo<FeeScheduleCategoryRedline>
        options={tableOptions}
        scrollButtons
        revealRowId={activeRowId}
        onDrawnRowsChange={(drawn) =>
          onDisplayedCategoriesChange?.(drawn?.flatMap((row) => (row.field.id ? [row.field.id] : [])))
        }
      />
    </Root>
  );
};

/**
 * The category a row shows. A row still in play shows where its decisions leave it; a removed one —
 * struck through, with nothing after the removal — shows the category that was removed.
 */
function displayedCategory(redline: FeeScheduleCategoryRedline): FeeScheduleCategoryEntry {
  return redline.resultingEntry ?? (redline.isRemoved ? redline.revisedEntry ?? redline.originalEntry : null);
}

/**
 * Every distinct description the cell is showing, in full: for an open redline that is both the
 * previous text and the one being asked for, so the hover answers the same question the clamped cell
 * raises.
 */
function fullDescriptionText(categoryRedline: FeeScheduleCategoryRedline): string {
  const descriptionRedline = categoryRedline.descriptionRedline;
  if (!descriptionRedline) return "";
  const values = [
    descriptionRedline.originalEntry,
    descriptionRedline.revisedEntry,
    descriptionRedline.currentEntry,
  ]
    .map((entry) => entry?.value?.trim())
    .filter((value): value is string => Boolean(value));
  return [...new Set(values)].join("\n");
}

type FeeScheduleCategoryEntry = FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency | null | undefined;
type RedlineTextSearchEntry = { value?: string } | null | undefined;
type RedlineTextSearchValue = {
  originalEntry?: RedlineTextSearchEntry;
  revisedEntry?: RedlineTextSearchEntry;
  currentEntry?: RedlineTextSearchEntry;
};

function getTextRedlineSearchValue(redline: RedlineTextSearchValue | undefined): string {
  if (!redline) return "";
  return [
    redline.currentEntry?.value,
    redline.revisedEntry?.value,
    redline.originalEntry?.value,
  ]
    .filter(Boolean)
    .join(" ");
}

function getCategoryRedlineNameValue(categoryRedline: FeeScheduleCategoryRedline): string {
  return getTextRedlineSearchValue(categoryRedline.nameRedline) || getCategoryEntrySearchValue(
    categoryRedline,
    (entry) => entry.name?.value ?? "",
  );
}

function getCategoryRedlineDescriptionValue(categoryRedline: FeeScheduleCategoryRedline): string {
  return getTextRedlineSearchValue(categoryRedline.descriptionRedline) || getCategoryEntrySearchValue(
    categoryRedline,
    (entry) => entry.description?.value ?? "",
  );
}

function getCategoryRedlineTypeValue(categoryRedline: FeeScheduleCategoryRedline): string {
  return getTextRedlineSearchValue(categoryRedline.typeRedline) || getCategoryEntrySearchValue(
    categoryRedline,
    (entry) => feeScheduleCategoryTypeLabel(entry.type),
  );
}

function getCategoryRedlineFeeValue(categoryRedline: FeeScheduleCategoryRedline): string {
  if (categoryRedline.feeRedline) {
    return [
      getFeeSearchValue(categoryRedline.feeRedline.currentEntry),
      getFeeSearchValue(categoryRedline.feeRedline.revisedEntry),
      getFeeSearchValue(categoryRedline.feeRedline.originalEntry),
    ]
      .filter(Boolean)
      .join(" ");
  }

  return getCategoryEntrySearchValue(
    categoryRedline,
    (entry) => entry instanceof FeeScheduleCategoryMaskedCurrency ? "Masked" : getFeeSearchValue(entry.fee),
  );
}

function getCategoryEntrySearchValue(
  categoryRedline: FeeScheduleCategoryRedline,
  getValue: (entry: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => string,
): string {
  return [
    getCategoryEntryValue(categoryRedline.currentEntry, getValue),
    getCategoryEntryValue(categoryRedline.revisedEntry, getValue),
    getCategoryEntryValue(categoryRedline.originalEntry, getValue),
  ]
    .filter(Boolean)
    .join(" ");
}

function getCategoryEntryValue(
  entry: FeeScheduleCategoryEntry,
  getValue: (entry: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => string,
): string {
  if (!entry) return "";
  return getValue(entry);
}

function getFeeSearchValue(fee: Fee | null | false | undefined): string {
  if (fee === false || fee === undefined) return "";
  if (fee === null) return "Deferred";
  return [
    fee.toString(),
    fee.rate?.amount?.toString(),
    fee.rate?.currency?.toString(),
    fee.unit ?? "Fixed",
  ]
    .filter(Boolean)
    .join(" ");
}

export default FeeScheduleRedlineSelection;

/** A flagged cell takes its issue's level: a blocker is an error, anything else a warning. */
function issueFlagSeverity(issue: ProposalIssue): FeeScheduleIssueFlagSeverity {
  return issue.level === ProposalIssueLevel.Critical ? "error" : "warning";
}
